import { ReactNode } from "react";
import { Box, Container, Flex, Text, IconButton, Select } from "@radix-ui/themes";
import { useTheme } from "./ThemeProvider";
import { useLanguage } from "./LanguageProvider";
import { LANGUAGES, Language } from "../lib/translations";

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function LanguageSelector() {
  const { language, setLanguage, t } = useLanguage();

  return (
    <Select.Root value={language} onValueChange={(value) => setLanguage(value as Language)}>
      <Select.Trigger
        variant="ghost"
        aria-label={t("selectLanguage")}
        className="language-trigger"
      />
      <Select.Content>
        {LANGUAGES.map((lang) => (
          <Select.Item key={lang.code} value={lang.code}>
            <Flex align="center" gap="2">
              <span className="flag-icon">{getFlagEmoji(lang.flag)}</span>
              <span>{lang.name}</span>
            </Flex>
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  );
}

function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

export function Layout({ children }: { children: ReactNode }) {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  const themeLabel = theme === "light" ? t("dark") : t("light");

  return (
    <Box className="app-layout">
      <Box asChild py="3" className="header">
        <header>
          <Container size="2">
            <Flex justify="between" align="center">
              <Flex align="center" gap="2">
                <Text size="5" className="coffee-emoji">☕</Text>
                <Text size="4" weight="bold" className="app-title">
                  {t("appTitle")}
                </Text>
              </Flex>
              <Flex align="center" gap="2">
                <LanguageSelector />
                <IconButton
                  variant="ghost"
                  size="2"
                  onClick={toggleTheme}
                  aria-label={t("switchTheme", { theme: themeLabel })}
                >
                  {theme === "light" ? <MoonIcon /> : <SunIcon />}
                </IconButton>
              </Flex>
            </Flex>
          </Container>
        </header>
      </Box>
      <Box asChild py="6" className="main-content">
        <main>
          <Container size="2">
            {children}
          </Container>
        </main>
      </Box>
      <Box asChild py="4" className="footer">
        <footer>
          <Container size="2">
            <Text size="1" color="gray" align="center" as="p">
              {t("footerText")}
            </Text>
          </Container>
        </footer>
      </Box>
    </Box>
  );
}
